import { BufferAttribute, BufferGeometry, Face, Float32BufferAttribute, InterleavedBufferAttribute, Vector2, Vector3 } from 'three';
import { MeshUtils } from './mesh-utils';

type Attribute = BufferAttribute | InterleavedBufferAttribute;

export class Face3 {
  constructor(
    public a: number,
    public b: number,
    public c: number,
    public normal: Vector3,
    public vertexNormals?: Vector3[]) {
  }

  public clone(): Face3 {
    return new Face3(this.a, this.b, this.c, this.normal.clone(), this.vertexNormals?.map((vector: Vector3): Vector3 => vector.clone()));
  }

  public static fromFace(face: Face) {
    return new Face3(face.a, face.b, face.c, face.normal);
  }

  public getVertices(vertices: Vector3[]): [ Vector3, Vector3, Vector3 ] {
    return [ vertices[this.a], vertices[this.b], vertices[this.c] ];
  }
}

export class GeometryUtils {

  public static reset(geometry: BufferGeometry) {
    geometry.setAttribute('position', new Float32BufferAttribute([], 3));
    geometry.setIndex(null);
    geometry.setAttribute('uv', new Float32BufferAttribute([], 2));
    geometry.setAttribute('normal', new Float32BufferAttribute([], 3));
  }

  public static setNeedsUpdate(geometry: BufferGeometry) {
    for (const attribute of Object.values(geometry.attributes)) {
      attribute.needsUpdate = true;
    }
  }

  public static getFaces(geometry: BufferGeometry): Face3[] {
    const positionAttribute = geometry.getAttribute('position');
    const normalAttribute = geometry.getAttribute('normal');
    const indexAttribute = geometry.getIndex();

    if (!positionAttribute) {
      return [];
    }

    if (indexAttribute) {
      return this.getFacesWithIndex(indexAttribute, positionAttribute, normalAttribute);
    } else {
      return this.getFacesWithoutIndex(positionAttribute, normalAttribute);
    }
  }

  private static getFacesWithIndex(indexAttribute: Attribute, positionAttribute: Attribute, normalAttribute: Attribute): Face3[] {

    const faces: Face3[] = [];

    for (let i = 0; i < indexAttribute.count; i += 3) {
      const a = indexAttribute.getX(i);
      const b = indexAttribute.getX(i + 1);
      const c = indexAttribute.getX(i + 2);

      // Calculate face normal
      const vA = new Vector3().fromBufferAttribute(positionAttribute, a);
      const vB = new Vector3().fromBufferAttribute(positionAttribute, b);
      const vC = new Vector3().fromBufferAttribute(positionAttribute, c);
      const cb = new Vector3().subVectors(vC, vB);
      const ab = new Vector3().subVectors(vA, vB);
      const faceNormal = new Vector3().crossVectors(cb, ab).normalize();

      // Extract vertex normals if available
      const vertexNormals = normalAttribute
        ? [
          new Vector3().fromBufferAttribute(normalAttribute, a),
          new Vector3().fromBufferAttribute(normalAttribute, b),
          new Vector3().fromBufferAttribute(normalAttribute, c),
        ]
        : undefined;

      faces.push(new Face3(a, b, c, faceNormal, vertexNormals));
    }

    return faces;
  }

  private static getFacesWithoutIndex(positionAttribute: Attribute, normalAttribute: Attribute): Face3[] {

    const faces: Face3[] = [];

    for (let i = 0; i < positionAttribute.count; i += 3) {
      if (i + 2 >= positionAttribute.count) break;
      const a = i;
      const b = i + 1;
      const c = i + 2;

      const vA = new Vector3(
        positionAttribute.getX(a),
        positionAttribute.getY(a),
        positionAttribute.getZ(a)
      );
      const vB = new Vector3(
        positionAttribute.getX(b),
        positionAttribute.getY(b),
        positionAttribute.getZ(b)
      );
      const vC = new Vector3(
        positionAttribute.getX(c),
        positionAttribute.getY(c),
        positionAttribute.getZ(c)
      );
      const cb = new Vector3().subVectors(vC, vB);
      const ab = new Vector3().subVectors(vA, vB);
      const faceNormal = new Vector3().crossVectors(cb, ab).normalize();

      const vertexNormals = normalAttribute
        ? [
          new Vector3(
            normalAttribute.getX(a),
            normalAttribute.getY(a),
            normalAttribute.getZ(a)
          ),
          new Vector3(
            normalAttribute.getX(b),
            normalAttribute.getY(b),
            normalAttribute.getZ(b)
          ),
          new Vector3(
            normalAttribute.getX(c),
            normalAttribute.getY(c),
            normalAttribute.getZ(c)
          ),
        ]
        : undefined;

      faces.push(new Face3(a, b, c, faceNormal, vertexNormals));
    }

    return faces;
  }

  /** Adds a triangle with the given vertexpositions, normals and UVs to the geometry */
  public static addTriangle(
    geometry: BufferGeometry,
    vertices: [Vector3, Vector3, Vector3],
    normals?: [Vector3, Vector3, Vector3],
    uvs?: [Vector2, Vector2, Vector2]
  ): void {
    let position = geometry.getAttribute('position') as Float32BufferAttribute;
    if (!position) {
      position = new Float32BufferAttribute([], 3);
      geometry.setAttribute('position', position);
    }
    const positions = Array.from(position.array);

    let normalAttr = geometry.getAttribute('normal') as Float32BufferAttribute;
    if (!normalAttr) {
      normalAttr = new Float32BufferAttribute([], 3);
      geometry.setAttribute('normal', normalAttr);
    }
    const normalArray = Array.from(normalAttr.array);

    let uvAttr = geometry.getAttribute('uv') as Float32BufferAttribute;
    if (!uvAttr) {
      uvAttr = new Float32BufferAttribute([], 2);
      geometry.setAttribute('uv', uvAttr);
    }
    const uvArray = Array.from(uvAttr.array);

    let index = geometry.getIndex();
    let indices: number[] | undefined = index ? Array.from(index.array) : undefined;
    const startIndex = positions.length / 3;

    for (let i = 0; i < 3; i++) {
      const v = vertices[i];
      positions.push(v.x, v.y, v.z);

      if (normals && normals[i]) {
        const n = normals[i];
        normalArray.push(n.x, n.y, n.z);
      } else {
        normalArray.push(0, 0, 0);
      }

      if (uvs && uvs[i]) {
        const uv = uvs[i];
        uvArray.push(uv.x, uv.y);
      } else {
        uvArray.push(0, 0);
      }
    }

    indices?.push(startIndex, startIndex + 1, startIndex + 2);

    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new Float32BufferAttribute(normalArray, 3));
    geometry.setAttribute('uv', new Float32BufferAttribute(uvArray, 2));
    if (indices) {
      geometry.setIndex(indices);
    }

    if (!normals) {
      geometry.computeVertexNormals();
    }
  }

  public static getVertices(geometry: BufferGeometry): Vector3[] {
    const positionAttribute = geometry.getAttribute('position') as BufferAttribute;
    const vertices: Vector3[] = [];

    if (!positionAttribute) return vertices;

    for (let i = 0; i < positionAttribute.count; i++) {
      const vertex = new Vector3(
        positionAttribute.getX(i),
        positionAttribute.getY(i),
        positionAttribute.getZ(i)
      );
      vertices.push(vertex);
    }

    return vertices;
  }

  public static getUvs(geometry: BufferGeometry): Vector2[] {
    const uvAttribute = geometry.getAttribute('uv') as BufferAttribute;
    const uvs: Vector2[] = [];

    if (!uvAttribute) return uvs;

    for (let i = 0; i < uvAttribute.count; i++) {
      const vertex = new Vector2(
        uvAttribute.getX(i),
        uvAttribute.getY(i),
      );
      uvs.push(vertex);
    }

    return uvs;
  }

  public static getNormals(geometry: BufferGeometry): Vector3[] {
    const normalAttribute = geometry.getAttribute('normal') as BufferAttribute;
    const vertices: Vector3[] = [];

    if (!normalAttribute) return vertices;

    for (let i = 0; i < normalAttribute.count; i++) {
      const vertex = new Vector3(
        normalAttribute.getX(i),
        normalAttribute.getY(i),
        normalAttribute.getZ(i)
      );
      vertices.push(vertex);
    }

    return vertices;
  }

  public static setVertices(geometry: BufferGeometry, vertices: Vector3[]): void {
    const positions: number[] = [];
    for (const vertex of vertices) {
      positions.push(vertex.x, vertex.y, vertex.z);
    }

    const attribute = new Float32BufferAttribute(positions, 3);
    attribute.needsUpdate = true;
    geometry.setAttribute('position', attribute);
  }

  public static addVertexToBufferGeometry(geometry: BufferGeometry, vertex: Vector3): void {
    if (!geometry.attributes.position) {
      geometry.setAttribute('position', new Float32BufferAttribute([], 3));
    }

    const position = geometry.attributes.position as Float32BufferAttribute;
    const newPositions = Array.from(position.array);

    // Neuen Vertex hinzufügen
    newPositions.push(vertex.x, vertex.y, vertex.z);

    // Position-Attribut aktualisieren
    geometry.setAttribute('position', new Float32BufferAttribute(newPositions, 3));
  }

  public static removeFaces(geometry: BufferGeometry, faceIndices: number[]): void {
    const position = geometry.getAttribute('position') as Float32BufferAttribute;
    const index = geometry.getIndex();

    if (!position) return;

    const faceSet = new Set(faceIndices);

    if (index) {
      // Mit Index-Attribut
      const newIndices: number[] = [];
      const newPositions: number[] = [];
      const vertexMap = new Map<number, number>();

      for (let i = 0; i < index.count; i += 3) {
        if (faceSet.has(i / 3)) continue;

        for (let j = 0; j < 3; j++) {
          const oldIndex = index.getX(i + j);
          if (!vertexMap.has(oldIndex)) {
            vertexMap.set(oldIndex, newPositions.length / 3);
            newPositions.push(
              position.getX(oldIndex),
              position.getY(oldIndex),
              position.getZ(oldIndex)
            );
          }
          newIndices.push(vertexMap.get(oldIndex)!);
        }
      }

      geometry.setAttribute('position', new Float32BufferAttribute(newPositions, 3));
      geometry.setIndex(newIndices);
      geometry.computeVertexNormals();
    } else {
      // Ohne Index-Attribut
      const newPositions: number[] = [];
      for (let i = 0; i < position.count; i += 3) {
        if (faceSet.has(i / 3)) continue;
        for (let j = 0; j < 3; j++) {
          const idx = i + j;
          newPositions.push(
            position.getX(idx),
            position.getY(idx),
            position.getZ(idx)
          );
        }
      }
      geometry.setAttribute('position', new Float32BufferAttribute(newPositions, 3));
      geometry.setIndex(null);
      geometry.computeVertexNormals();
    }
  }

  public static getFaceVertexUvs(geometry: BufferGeometry): Vector2[][] {
    const uvAttribute = geometry.attributes.uv as BufferAttribute;
    if (!uvAttribute) return [];

    const uvs: Vector2[] = [];
    for (let i = 0; i < uvAttribute.count; i++) {
      uvs.push(new Vector2(uvAttribute.getX(i), uvAttribute.getY(i)));
    }

    const result: Vector2[][] = [];
    const index = geometry.getIndex();

    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        const a = index.getX(i);
        const b = index.getX(i + 1);
        const c = index.getX(i + 2);
        result.push([uvs[a], uvs[b], uvs[c]]);
      }
    } else {
      for (let i = 0; i < uvs.length; i += 3) {
        if (i + 2 >= uvs.length) break;
        result.push([uvs[i], uvs[i + 1], uvs[i + 2]]);
      }
    }

    return result;
  }

  public static getPlaneGeometryByFace(geometry: BufferGeometry, face: Face3, faceIndexesToDelete: number[] = []): BufferGeometry {

    const faces = GeometryUtils.getFaces(geometry);
    const vertices = GeometryUtils.getVertices(geometry);
    const uvs = GeometryUtils.getUvs(geometry);
    const normals = GeometryUtils.getNormals(geometry);

    // Potential faces of the plane need to have a similar normal
    let potentialFaces = faces.filter((f) => MeshUtils.areVectorsSimilarEnough(f.normal, face.normal));
    let match: boolean | undefined;

    // Track included face indexes for faster lookup
    const includedFaceIndexes = new Set<number>();
    includedFaceIndexes.add(face.a);
    includedFaceIndexes.add(face.b);
    includedFaceIndexes.add(face.c);
    const resultFaces: Face3[] = [];

    do {
      match = false;

      const newPotentialFaces: Face3[] = [];

      for (const potentialFace of potentialFaces) {

        // Check if any vertex is already included, otherwise check if the face is touching any already included face
        if (includedFaceIndexes.has(potentialFace.a)
          || includedFaceIndexes.has(potentialFace.b)
          || includedFaceIndexes.has(potentialFace.c)
          || MeshUtils.vectorListsAreTouching(
            [ ...includedFaceIndexes ].map((faceIndex) => vertices[faceIndex]),
            potentialFace.getVertices(vertices),
          )
        ) {
          includedFaceIndexes.add(potentialFace.a);
          includedFaceIndexes.add(potentialFace.b);
          includedFaceIndexes.add(potentialFace.c);
          resultFaces.push(potentialFace);

          // If there is a new match, we have to check all non-matching faces again
          match = true;
        } else {
          newPotentialFaces.push(potentialFace);
        }
      }

      potentialFaces = newPotentialFaces;

    } while (match);

    const resultGeometry = new BufferGeometry();
    resultGeometry.setAttribute('position', new Float32BufferAttribute([], 3));
    for (const resultFace of resultFaces) {
      GeometryUtils.addTriangle(
        resultGeometry,
        [ vertices[resultFace.a].clone(), vertices[resultFace.b].clone(), vertices[resultFace.c].clone() ],
        [ normals[resultFace.a].clone(), normals[resultFace.b].clone(), normals[resultFace.c].clone() ],
        [ uvs[resultFace.a].clone(), uvs[resultFace.b].clone(), uvs[resultFace.c].clone() ],
      );

      const faceIndex: number = MeshUtils.findFaceIndex(resultFace, faces);
      faceIndexesToDelete.push(faceIndex);
    }

    return resultGeometry;
  }
}
